import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";

const INITIAL_VALUES = {
  email: "",
  first_name: "",
  last_name: "",
  role: "user",
};

function InternalInvitationsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const currentInternalUser = useSelector((state) => state.current_internal_user);

  const [values, setValues] = useState(INITIAL_VALUES);
  const [loading, setLoading] = useState(false);

  const isAdmin = useMemo(() => currentInternalUser?.role === "admin", [currentInternalUser]);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      backgroundColor: "hsl(220 20% 95%)",
      borderRadius: "0.5rem",
      "& fieldset": {
        borderColor: "hsl(214 20% 90%)",
      },
      "&:hover fieldset": {
        borderColor: "hsl(214 20% 84%)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "hsl(220 18% 20%)",
      },
    },
    "& .MuiInputBase-input": {
      fontSize: "14px",
    },
    "& .MuiSelect-select": {
      fontSize: "14px",
    },
  };

  const labelSx = {
    display: "block",
    fontSize: "12px",
    fontFamily: "Segoe UI Regular, sans-serif",
    color: "hsl(220 10% 46%)",
    mb: "0.5rem !important",
  };

  const requiredAsteriskSx = {
    color: "hsl(0 84.2% 60.2%)",
    fontWeight: 600,
    ml: 0.5,
    fontSize: "11px",
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!values.email || !values.first_name || !values.last_name) {
      enqueueSnackbar("Please fill in all required fields.", { variant: "error" });
      return;
    }
    if (!isAdmin) {
      enqueueSnackbar("Only admins can send invitations.", { variant: "error" });
      return;
    }

    setLoading(true);
    sh.post("/internal/invitation", {
      authenticity_token,
      internal_user: {
        email: values.email,
        first_name: values.first_name,
        last_name: values.last_name,
        role: values.role,
      },
    })
      .then(() => {
        enqueueSnackbar(`Invitation sent to ${values.email}`, { variant: "success" });
        setValues(INITIAL_VALUES);
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          enqueueSnackbar("You are not authorized to send invitations.", { variant: "error" });
        } else if (err.response?.status === 409) {
          const code = err.response?.data?.code;
          if (code === "ALREADY_INTERNAL_USER") {
            enqueueSnackbar("This email already belongs to an internal user.", { variant: "warning" });
          } else if (code === "ALREADY_INVITED") {
            enqueueSnackbar("This email has already been invited.", { variant: "warning" });
          } else {
            enqueueSnackbar("Invitation conflict detected.", { variant: "warning" });
          }
        } else {
          enqueueSnackbar("Failed to send invitation.", { variant: "error" });
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100dvh",
        backgroundColor: "hsl(210 20% 98%)",
        px: { xs: 2, sm: 3 },
        display: "flex",
        alignItems: "center",
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: "42rem", mx: "auto", px: 0 }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: "hsl(0 0% 100%)",
            border: "1px solid hsl(214 20% 90%)",
            borderRadius: "0.75rem",
            p: { xs: 3, sm: 4 },
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
          }}
        >
          <Typography sx={{ fontSize: "20px", lineHeight: 1.4, fontFamily: "Segoe UI Bold, sans-serif", color: "hsl(220 20% 10%)", mb: 0.5 }}>
            Invite Internal User
          </Typography>
          <Typography sx={{ fontSize: "14px", fontFamily: "Segoe UI Regular, sans-serif", color: "hsl(220 14% 32%)", mb: 4 }}>
            Send an invitation email so a teammate can set their password and access the internal dashboard.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box>
              <Typography component="label" htmlFor="invite-email" sx={labelSx}>
                Email
                <Box component="span" sx={requiredAsteriskSx}>*</Box>
              </Typography>
              <TextField
                id="invite-email"
                required
                name="email"
                type="email"
                placeholder="name@squarehub.eu"
                value={values.email}
                onChange={handleInputValue}
                fullWidth
                disabled={loading || !isAdmin}
                sx={inputSx}
              />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <Box>
                <Typography component="label" htmlFor="invite-first-name" sx={labelSx}>
                  First name
                  <Box component="span" sx={requiredAsteriskSx}>*</Box>
                </Typography>
                <TextField
                  id="invite-first-name"
                  required
                  name="first_name"
                  placeholder="First name"
                  value={values.first_name}
                  onChange={handleInputValue}
                  fullWidth
                  disabled={loading || !isAdmin}
                  sx={inputSx}
                />
              </Box>

              <Box>
                <Typography component="label" htmlFor="invite-last-name" sx={labelSx}>
                  Last name
                  <Box component="span" sx={requiredAsteriskSx}>*</Box>
                </Typography>
                <TextField
                  id="invite-last-name"
                  required
                  name="last_name"
                  placeholder="Last name"
                  value={values.last_name}
                  onChange={handleInputValue}
                  fullWidth
                  disabled={loading || !isAdmin}
                  sx={inputSx}
                />
              </Box>
            </Box>

            <Box>
              <Typography component="label" htmlFor="invite-role" sx={labelSx}>
                Role
              </Typography>
              <FormControl fullWidth disabled={loading || !isAdmin} sx={inputSx}>
                <Select
                  id="invite-role"
                  name="role"
                  value={values.role}
                  onChange={handleInputValue}
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="coordinator">Coordinator</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
              {!isAdmin && (
                <FormHelperText sx={{ mt: 1 }}>
                  Only admins are allowed to send invitations.
                </FormHelperText>
              )}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1.5 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !isAdmin}
                sx={{
                  height: 44,
                  px: 3,
                  borderRadius: "0.5rem",
                  textTransform: "none",
                  fontFamily: "Segoe UI Semibold, sans-serif",
                  backgroundColor: "hsl(220 18% 20%)",
                  boxShadow: "none",
                  color: "hsl(0 0% 100%)",
                  "&:hover": {
                    backgroundColor: "hsl(220 18% 17%)",
                    boxShadow: "none",
                  },
                }}
              >
                {loading ? "Sending..." : "Send invitation"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default InternalInvitationsPage;
